import { createFileRoute } from "@tanstack/react-router";

import { SellerPromo } from "../components/seller/SellerPromo";
import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { ContactForm } from "../components/home/ContactForm";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { Showreel } from "../components/home/Showreel";
import { EcomHero } from "../components/ecom/EcomHero";
import { Portfolio } from "../components/shared/Portfolio";
import { ecomCases } from "../content/cases";
import { EcomOffer } from "../components/ecom/EcomOffer";
import { EcomProcess } from "../components/ecom/EcomProcess";
import { EcomPricing } from "../components/ecom/EcomPricing";
import { EcomTech } from "../components/ecom/EcomTech";
import { EcomLinks } from "../components/ecom/EcomLinks";
import { Faq } from "../components/shared/Faq";
import { Reviews } from "../components/shared/Reviews";
import { Team } from "../components/shared/Team";
import { buildMeta, breadcrumbJsonLd, serviceJsonLd, SITE_URL } from "../lib/site";

const title = "Разработка интернет-магазина под ключ — стоимость | HAY.agency";
const description =
  "Создаём интернет-магазины под ключ: аналитика, UX, дизайн, каталог, оплата, доставка, интеграции с 1С и CRM, SEO и запуск. Стоимость — от 900 000 ₽.";
const ogTitle = "Разработка интернет-магазина под ключ — HAY.agency";
const ogDescription =
  "Проектируем и запускаем интернет-магазины: каталог, оплата, доставка, интеграции, аналитика и SEO.";

const faqItems = [
  {
    q: "Сколько стоит разработка интернет-магазина?",
    a: "Стоимость начинается от 900 000 ₽. Финальная оценка зависит от функциональности, каталога, дизайна и интеграций. Точную смету фиксируем после брифа и предварительной оценки проекта.",
  },
  {
    q: "Сколько времени занимает разработка?",
    a: "Срок запуска — от 3,5 месяцев. Он зависит от масштаба проекта, скорости согласований и готовности контента. Крупные каталоги и сложные интеграции увеличивают сроки.",
  },
  {
    q: "На какой CMS вы разрабатываете интернет-магазины?",
    a: "Чаще всего это разработка интернет-магазина на 1С-Битрикс — она удобна для каталога, обмена с 1С и работы контент-менеджеров. Также работаем с WordPress, Shopify и кастомными решениями на PHP, Python и Node-стеке, если задача этого требует.",
  },
  {
    q: "Можно ли интегрировать магазин с 1С и CRM?",
    a: "Да, настраиваем обмен с 1С по товарам, ценам, остаткам и заказам, а также интеграции с RetailCRM, amoCRM, платёжными сервисами и службами доставки. Перечень интеграций согласуем на этапе аналитики: поддержку произвольной системы подтверждаем после изучения её API.",
  },
  {
    q: "Входит ли SEO в разработку интернет-магазина?",
    a: "При разработке закладывается техническая SEO-основа: структура каталога, ЧПУ, метатеги, скорость, микроразметка и корректная индексация. Полноценное продвижение — отдельная услуга, её мы ведём на странице /seo.",
  },
  {
    q: "Можно ли перенести существующий интернет-магазин?",
    a: "Да. Сначала проводим аудит данных, структуры, текущей CMS и интеграций, затем планируем перенос каталога, заказов и адресов страниц с редиректами. Так сохраняются накопленные позиции и история продаж.",
  },
  {
    q: "Что происходит после запуска?",
    a: "Остаёмся на технической поддержке, развиваем функциональность, следим за аналитикой и корректируем сценарии покупки. При необходимости подключаем SEO и рекламу для роста продаж.",
  },
];

export const Route = createFileRoute("/ecom")({
  head: () => {
    const base = buildMeta({
      title,
      description,
      path: "/ecom",
      image: `${SITE_URL}/og/services/ecom.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Разработка интернет-магазинов в HAY.agency",
      locale: "ru_RU",
    });
    return {
      ...base,
      meta: base.meta.map((tag) => {
        if (tag["property"] === "og:title") return { property: "og:title", content: ogTitle };
        if (tag["property"] === "og:description")
          return { property: "og:description", content: ogDescription };
        return tag;
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${SITE_URL}/ecom#webpage`,
                url: `${SITE_URL}/ecom`,
                name: title,
                description,
                inLanguage: "ru-RU",
                isPartOf: { "@id": `${SITE_URL}/#website` },
                breadcrumb: { "@id": `${SITE_URL}/ecom#breadcrumbs` },
                mainEntity: { "@id": `${SITE_URL}/ecom#service` },
              },
              serviceJsonLd({
                name: "Разработка интернет-магазинов",
                description,
                path: "/ecom",
                id: `${SITE_URL}/ecom#service`,
                serviceType: "Разработка интернет-магазинов под ключ",
                providerId: `${SITE_URL}/#organization`,
                inGraph: true,
              }),
              breadcrumbJsonLd(
                [
                  ["Главная", "/"],
                  ["Интернет-магазины", "/ecom"],
                ],
                { id: `${SITE_URL}/ecom#breadcrumbs`, inGraph: true },
              ),
              {
                "@type": "FAQPage",
                "@id": `${SITE_URL}/ecom#faq`,
                mainEntity: faqItems.map((item) => ({
                  "@type": "Question",
                  name: item.q,
                  acceptedAnswer: { "@type": "Answer", text: item.a },
                })),
              },
            ],
          }),
        },
      ],
    };
  },
  component: EcomPage,
});

function EcomPage() {
  return (
    <ServicePageLayout breadcrumb="Интернет-магазины" jsonLd={false}>
      <EcomHero />
      <div className="pb-10 lg:pb-[74px]">
        <ClientsMarquee bare />
      </div>
      <Showreel />
      <Portfolio
        items={ecomCases}
        lead="Работаем не со всеми: помогаем тем компаниям, кому действительно можем дать результат и уверены в этом. Мы не просто посредники: становимся частью вашей команды, вовлекаясь в бизнес на 100%."
      />
      <SellerPromo variant="strip" />
      <EcomOffer />
      <EcomProcess />
      <EcomPricing />
      <EcomTech />
      <Faq
        compact
        title="Частые вопросы о разработке интернет-магазина"
        lead="Стоимость, сроки, CMS и интеграции — коротко."
        items={faqItems}
      />
      <EcomLinks />
      <Reviews />
      <Team />
      <ContactForm />
    </ServicePageLayout>
  );
}
